import * as React from 'react'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import * as api from '@services/api'
import type { AuthUser } from './types'
import { clearToken, getStoredUser, replaceStoredToken, storeToken } from './authStore'

type AuthCtx = {
  user: AuthUser | null
  login: (username: string, password: string, remember: boolean) => Promise<void>
  replaceToken: (token: string) => void
  logout: () => void
}

const Ctx = createContext<AuthCtx | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => getStoredUser())

  useEffect(() => {
    setUser(getStoredUser())
  }, [])

  const login = React.useCallback(async (username: string, password: string, remember: boolean) => {
    const token = await api.login(username, password)
    storeToken(token, remember)
    setUser(getStoredUser())
  }, [])

  const logout = React.useCallback(() => {
    clearToken()
    setUser(null)
  }, [])

  const replaceToken = React.useCallback((token: string) => {
    replaceStoredToken(token)
    setUser(getStoredUser())
  }, [])

  const value = useMemo(
    () => ({ user, login, replaceToken, logout }),
    [user, login, replaceToken, logout],
  )
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('AuthProvider not mounted')
  return ctx
}
